/*
 *
 * ModeBar
 *
 */

import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useInjectReducer } from 'utils/injectReducer';
import { createStructuredSelector } from 'reselect';
import reducer, { ConfiguratorMode } from './reducer';
import { RootState } from './types';
import { makeSelectConfiguratorMode } from './selectors';
import { switchModeAction } from './actions';
import {
  FaEraser,
  FaTrash,
  FiMousePointer,
  FiTrash,
  FiTrash2,
  IoIosColorFill,
} from 'react-icons/all';
import ReactTooltip from 'react-tooltip';
import styled from 'styled-components/macro';
// eslint-disable-next-line no-restricted-imports
import { css } from 'styled-components';
import { useTranslation } from 'react-i18next';
import useOnClickOutside from 'use-onclickoutside';
import { setColorsPanelAction } from '../Configurator/actions/general';
import { ActivePanel } from '../Configurator/constants/general';
import HandlesModeSelector from './HandlesModeSelector';
import useMedia from 'use-media';
import { shelvesSelectionSuccessAction } from '../Configurator/actions/selection';
import { makeSelectActiveTab } from '../Header/selectors';

interface StateProps {
  activeMode: ConfiguratorMode;
}

const stateSelector = createStructuredSelector<RootState, StateProps>({
  activeMode: makeSelectConfiguratorMode(),
});

interface Props {}

function ModeBar(props: Props) {
  useInjectReducer({ key: 'modeBar', reducer: reducer });
  const dispatch = useDispatch();
  const { activeMode } = useSelector(stateSelector);
  const { t } = useTranslation();
  const [isOpen, setOpen] = useState(false);
  const ref = useRef(null);
  useOnClickOutside(ref, () => {
    setOpen(false);
  });
  const activeTabName = useSelector(makeSelectActiveTab());

  const isMobile = useMedia({ maxWidth: 1023 });
  useEffect(() => {
    if (!isMobile && activeMode !== ConfiguratorMode.selector) {
      dispatch(switchModeAction(ConfiguratorMode.selector));
      dispatch(setColorsPanelAction());
    }
  }, [isMobile]);
  if (isMobile) return null;

  const hide = () => {
    setOpen(false);
  };

  return (
    <>
      <div className="absolute top-0 left-0 mt-20 bg-white text-main flex flex-col border border-gray-400">
        <StyledModeButton
          onClick={() => {
            dispatch(switchModeAction(ConfiguratorMode.selector));
            dispatch(setColorsPanelAction());
            dispatch(shelvesSelectionSuccessAction([]));
          }}
          className="p-2 hover:bg-gray-200 "
          data-for="mode"
          data-tip={t('Selector')}
          selected={activeMode === ConfiguratorMode.selector ? 1 : 0}
        >
          <FiMousePointer className="h-6 w-6" />
        </StyledModeButton>
        <StyledModeButton
          onClick={() => {
            dispatch(switchModeAction(ConfiguratorMode.paint, activeTabName));
            dispatch(setColorsPanelAction(ActivePanel.paint));
          }}
          className="p-2 hover:bg-gray-200"
          data-for="mode"
          data-tip={t('Paint')}
          selected={activeMode === ConfiguratorMode.paint ? 1 : 0}
        >
          <IoIosColorFill className="h-6 w-6" />
        </StyledModeButton>
        <HandlesModeSelector activeMode={activeMode} />
        <StyledModeButton
          onClick={() => {
            dispatch(switchModeAction(ConfiguratorMode.delete));
            dispatch(shelvesSelectionSuccessAction([]));
            dispatch(setColorsPanelAction());
          }}
          className="p-2 hover:bg-gray-200 "
          data-for="mode"
          data-tip={t('Delete')}
          selected={activeMode === ConfiguratorMode.delete ? 1 : 0}
        >
          <FaTrash className="h-5 w-5" />
        </StyledModeButton>
      </div>
      <ReactTooltip id="mode" place="right" type="dark" />
    </>
  );
}

export default ModeBar;

export const StyledModeButton = styled.button<{ selected: number }>`
  display: flex;
  justify-content: center;
  ${p =>
    p.selected &&
    css`
      background-color: ${p => p.theme.main};
      color: ${p => p.theme.white};
    `};
`;
